<script setup lang="ts">
import CreateAudienceModal from '@/views/audience/components/CreateAudienceModal.vue'
import AudienceTemplate from '@/views/audience/components/AudienceTemplate.vue'

import useAudienceTemplates from '@/views/audience/composables/templates'

const { t } = useI18n()
const route = useRoute()

const { categories, templates } = useAudienceTemplates()

const isCreateAudienceModalOpen = ref(false)
const segmentName = ref('')
const selectedCategory = ref<string>('all')
const cardWidth = ref<string>('auto')
const firstCardRef = ref<HTMLElement | null>(null)

const fromMarketingFlow = computed(() => route.query.fromMarketingFlow as string | undefined)
const audienceTemplate = computed(() => route.query.audienceTemplate as string | undefined)

const filteredTemplates = computed(() => {
  if (selectedCategory.value === 'all') {
    return templates.value
  }

  if (selectedCategory.value === 'recommended') {
    return templates.value.filter((template) => template.isRecommended === true)
  }

  return templates.value.filter((template) => template.category === selectedCategory.value)
})

const goToCreateAudience = () => {
  isCreateAudienceModalOpen.value = true
}

const closeCreateAudienceModal = () => {
  isCreateAudienceModalOpen.value = false
}

const selectCategory = (categoryId: string) => {
  selectedCategory.value = categoryId
}

onMounted(() => {
  if (firstCardRef.value?.[0]) {
    const width = firstCardRef.value[0].getBoundingClientRect().width
    cardWidth.value = `${width}px`
  }
})
</script>

<template>
  <div>
    <TheDetailHeader
      :title="t('pages.audience.templatesPage.title')"
      :parent="{ label: t('pages.customer.audiences.header.crumbs'), to: { name: 'AudienceList' } }"
    >
      <template #actions>
        <Button variant="default-primary" @click="goToCreateAudience">
          {{ t('pages.audience.templatesPage.button') }}
        </Button>
      </template>
    </TheDetailHeader>

    <div class="px-6 pb-6 sm:px-12 sm:pb-12">
      <h2 class="mb-5 font-medium text-foreground">{{ t('pages.audience.templatesPage.description') }}</h2>
      <BaseTemplateFilters
        :categories="categories"
        :selectedCategory="selectedCategory"
        @select-category="selectCategory"
      />

      <TransitionGroup name="template-cards" tag="div" class="grid md:grid-cols-3 gap-4">
        <div v-for="template in filteredTemplates" :key="template.id" ref="firstCardRef" :style="{ width: cardWidth }">
          <AudienceTemplate
            :template="template"
            :from-marketing-flow="fromMarketingFlow"
            :audience-template="audienceTemplate"
          />
        </div>
      </TransitionGroup>
    </div>

    <CreateAudienceModal
      v-model:segment-name="segmentName"
      :is-open="isCreateAudienceModalOpen"
      @close="closeCreateAudienceModal"
    />
  </div>
</template>

<style scoped>
.template-cards-enter-active {
  transition: all 0.4s ease-out;
}

.template-cards-leave-active {
  transition: all 0.3s ease-in;
  position: absolute;
}

.template-cards-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.template-cards-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.98);
}

.template-cards-move {
  transition: transform 0.4s ease;
}
</style>
